import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { UserUpdateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { RoleCreate } from "../../../role/components/create/RoleCreate";
import { RoleCreateStore } from "../../../role/store";
import { Language } from "../../../../api/graphql";
import { AvatarField } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { InputPhone } from "../../../common/controls";
import { FormControl } from "@mui/material";
import { IdCardType } from "../../../../api/graphql";
import { InputStringMask } from "../../../common/controls";
import { Gender } from "../../../../api/graphql";

export type SiteUserUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  name: string;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  nameError?: string;
  email: string;
  emailSelectedValue?: { displayId: string; id: string } | null;
  onEmailChange?: (value: string) => void;
  emailError?: string;
  roleSelectedValue?: { displayId: string; id: string } | null;
  onRoleChange?: (value: string) => void;
  onRoleSelected?: (value: { displayId: string; id: string } | null) => void;
  roleOptions?: { displayId: string; id: string }[];
  roleError?: string;
  languageOptions: { id: string; displayId: string }[];
  languageError?: string;
  language: Language;
  onLanguageChange?: (value: Language) => void;
  avatarData?: any;
  avatarEntityId: string;
  avatarPreviewUrl?: string | null;
  onAvatarChange: (blob: Blob) => void;
  onAvatarDelete: () => void;
  active: boolean;
  activeSelectedValue?: { displayId: string; id: string } | null;
  onActiveChange?: (value: boolean) => void;
  activeError?: string;
  cellphone: string;
  cellphoneSelectedValue?: { displayId: string; id: string } | null;
  onCellphoneChange?: (value: string) => void;
  cellphoneError?: string;
  twoFaseAuth: boolean;
  twoFaseAuthSelectedValue?: { displayId: string; id: string } | null;
  onTwoFaseAuthChange?: (value: boolean) => void;
  twoFaseAuthError?: string;
  idCardTypeOptions: { id: string; displayId: string }[];
  idCardTypeError?: string;
  idCardType: IdCardType | "";
  onIdCardTypeChange?: (value: IdCardType | "") => void;
  idCard: string;
  idCardSelectedValue?: { displayId: string; id: string } | null;
  onIdCardChange?: (value: string) => void;
  idCardError?: string;
  genderOptions: { id: string; displayId: string }[];
  genderError?: string;
  gender: Gender | "";
  onGenderChange?: (value: Gender | "") => void;
  userUpdateStore: UserUpdateStore;
};

export const SiteUserUpdateForm: React.FC<SiteUserUpdateFormProps> = observer(
  ({
    onSave = () => {},
    onCancel = () => {},
    saving = false,
    showToolbox = true,
    showSaveFooter,
    name = "",
    nameError,
    onNameChange = () => {},
    email = "",
    emailError,
    onEmailChange = () => {},
    onRoleSelected = () => {},
    roleOptions = [],
    roleSelectedValue = null,
    roleError,
    onRoleChange = () => {},
    languageOptions = [],
    languageError,
    language = "" as Language,
    onLanguageChange = () => {},
    avatarData,
    avatarEntityId,
    avatarPreviewUrl,
    onAvatarChange,
    onAvatarDelete,
    active = false,
    activeError,
    onActiveChange = () => {},
    cellphone = "",
    cellphoneError,
    onCellphoneChange = () => {},
    twoFaseAuth = false,
    twoFaseAuthError,
    onTwoFaseAuthChange = () => {},
    idCardTypeOptions = [],
    idCardTypeError,
    idCardType = "" as IdCardType | "",
    onIdCardTypeChange = () => {},
    idCard = "",
    idCardError,
    onIdCardChange = () => {},
    genderOptions = [],
    genderError,
    gender = "" as Gender | "",
    onGenderChange = () => {},
    userUpdateStore,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    return (
      <Grid>
        {showToolbox && (
          <FormToolbox
            saveAndCloseLabel={langStore.saveEntity}
            closeLabel={langStore.cancelUpdatedEntity}
            saveForm={() => onSave()}
            saveDisabled={saving}
            closeForm={() => onCancel()}
          />
        )}
        <Box>
          <FormExtras
            entity="User"
            mode="update"
            values={{}}
            options={{ role: roleOptions }}
            onPatch={(campo: string, valor: any) => {
              switch (campo) {
                case "name":
                  onNameChange?.(valor);
                  break;
                case "email":
                  onEmailChange?.(valor);
                  break;
                case "role":
                  onRoleSelected?.(valor);
                  break;
                case "language":
                  onLanguageChange?.(valor);
                  break;
                case "active":
                  onActiveChange?.(valor);
                  break;
                case "cellphone":
                  onCellphoneChange?.(valor);
                  break;
                case "twoFaseAuth":
                  onTwoFaseAuthChange?.(valor);
                  break;
                case "idCardType":
                  onIdCardTypeChange?.(valor);
                  break;
                case "idCard":
                  onIdCardChange?.(valor);
                  break;
                case "gender":
                  onGenderChange?.(valor);
                  break;
              }
            }}
          />
          <GroupContainer
            id="main"
            errorText={userUpdateStore.getCardErrors("main")}
            title={langStore.groupSiteUserMainLabel}
            collapsible={true}
            expanded={userUpdateStore.getCardState("main")}
            toggleExpand={() => userUpdateStore.toggleCardState("main")}
          >
            <FormControl fullWidth>
              <InputString
                autoFocus
                value={name}
                error={Boolean(nameError)}
                warning={
                  !nameError && Boolean(userUpdateStore.warnings["name"])
                }
                helperText={
                  nameError || userUpdateStore.warnings["name"]?.message
                }
                label={langStore.UserNameLabel}
                onChange={(value: string) => onNameChange(value)}
                description={langStore.UserNameDescription}
              />
              <InputString
                value={email}
                error={Boolean(emailError)}
                warning={
                  !emailError && Boolean(userUpdateStore.warnings["email"])
                }
                helperText={
                  emailError || userUpdateStore.warnings["email"]?.message
                }
                label={langStore.UserEmailLabel}
                onChange={(value: string) => onEmailChange(value)}
                description={langStore.UserEmailDescription}
              />
              <InputReference
                id="role"
                label={langStore.UserRoleLabel}
                error={Boolean(roleError)}
                helperText={
                  roleError || userUpdateStore.warnings["role"]?.message
                }
                selectedValue={roleSelectedValue}
                onChange={(value: string) => onRoleChange(value)}
                onSelect={(value: { displayId: string; id: string } | null) =>
                  onRoleSelected(value)
                }
                options={roleOptions}
                description={langStore.UserRoleDescription}
                nullText={langStore.optionalFieldHint}
                canCreate={rootStore.authStore.hasPermissions("role_create")}
                renderCreateDialog={(dialogProps) => (
                  <CreateEntityDialog
                    {...dialogProps}
                    createStore={(rootStore) => new RoleCreateStore(rootStore)}
                    renderForm={(store) => (
                      <RoleCreate
                        roleCreateStore={store}
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    )}
                    saveMethod="saveNewRole"
                    cancelMethod="cancelNewRole"
                  />
                )}
              />
              <InputReference
                id="language"
                disabled={false}
                error={Boolean(languageError)}
                helperText={
                  languageError || userUpdateStore.warnings["language"]?.message
                }
                label={langStore.UserLanguageLabel}
                options={languageOptions}
                selectedValue={
                  language
                    ? {
                        id: language,
                        displayId:
                          languageOptions.find(
                            (o: { id: string }) => o.id === language
                          )?.displayId || language,
                      }
                    : null
                }
                onChange={() => {}}
                onSelect={(value: { id: string; displayId: string } | null) =>
                  onLanguageChange((value?.id as Language) || ("" as Language))
                }
              />
              <AvatarField
                avatarData={avatarData}
                entityName="User"
                fieldName="avatar"
                entityId={avatarEntityId}
                previewUrl={avatarPreviewUrl}
                label={langStore.UserAvatarLabel}
                onAvatarChange={(blob: Blob) => onAvatarChange(blob)}
                onAvatarDelete={() => onAvatarDelete()}
              />
              <InputBoolean
                value={active}
                error={Boolean(activeError)}
                helperText={
                  activeError || userUpdateStore.warnings["active"]?.message
                }
                label={langStore.UserActiveLabel}
                description={langStore.UserActiveDescription}
                onChange={(value: boolean) => onActiveChange(value)}
              />
              <InputPhone
                value={cellphone}
                error={Boolean(cellphoneError)}
                helperText={
                  cellphoneError ||
                  userUpdateStore.warnings["cellphone"]?.message
                }
                label={langStore.UserCellphoneLabel}
                onChange={(value: string) => onCellphoneChange(value)}
                nullText={langStore.optionalFieldHint}
                description={langStore.UserCellphoneDescription}
              />
              <InputBoolean
                value={twoFaseAuth}
                error={Boolean(twoFaseAuthError)}
                helperText={
                  twoFaseAuthError ||
                  userUpdateStore.warnings["twoFaseAuth"]?.message
                }
                label={langStore.UserTwoFaseAuthLabel}
                description={langStore.UserTwoFaseAuthDescription}
                onChange={(value: boolean) => onTwoFaseAuthChange(value)}
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="details"
            errorText={userUpdateStore.getCardErrors("details")}
            title={langStore.groupSiteUserDetailsLabel}
            collapsible={true}
            expanded={userUpdateStore.getCardState("details")}
            toggleExpand={() => userUpdateStore.toggleCardState("details")}
          >
            <FormControl fullWidth>
              <InputReference
                id="idCardType"
                disabled={false}
                error={Boolean(idCardTypeError)}
                helperText={
                  idCardTypeError ||
                  userUpdateStore.warnings["idCardType"]?.message
                }
                label={langStore.UserIdCardTypeLabel}
                options={idCardTypeOptions}
                nullText={langStore.optionalFieldHint}
                selectedValue={
                  idCardType
                    ? {
                        id: idCardType,
                        displayId:
                          idCardTypeOptions.find(
                            (o: { id: string }) => o.id === idCardType
                          )?.displayId || idCardType,
                      }
                    : null
                }
                onChange={() => {}}
                onSelect={(value: { id: string; displayId: string } | null) =>
                  onIdCardTypeChange(
                    (value?.id as IdCardType | "") || ("" as IdCardType | "")
                  )
                }
              />
              <InputStringMask
                value={idCard}
                error={Boolean(idCardError)}
                warning={
                  !idCardError && Boolean(userUpdateStore.warnings["idCard"])
                }
                helperText={
                  idCardError || userUpdateStore.warnings["idCard"]?.message
                }
                label={langStore.UserIdCardLabel}
                onChange={(value: string) => onIdCardChange(value)}
                conditionalMasks={[
                  {
                    mask: "999999999",
                    masks: ["999999999", "99999999", "9999999"],
                    when: {
                      field: "idCardType",
                      operator: "in",
                      values: ["dni"],
                    },
                  },
                  {
                    mask: "99-99999999-9",
                    masks: ["99-99999999-9", "99-9999999-9"],
                    when: {
                      field: "idCardType",
                      operator: "in",
                      values: ["cuitCuil"],
                    },
                  },
                ]}
                conditionValues={{ idCardType: idCardType }}
                nullText={langStore.optionalFieldHint}
                description={langStore.UserIdCardDescription}
              />
              <InputReference
                id="gender"
                disabled={false}
                error={Boolean(genderError)}
                helperText={
                  genderError || userUpdateStore.warnings["gender"]?.message
                }
                label={langStore.UserGenderLabel}
                options={genderOptions}
                nullText={langStore.optionalFieldHint}
                selectedValue={
                  gender
                    ? {
                        id: gender,
                        displayId:
                          genderOptions.find(
                            (o: { id: string }) => o.id === gender
                          )?.displayId || gender,
                      }
                    : null
                }
                onChange={() => {}}
                onSelect={(value: { id: string; displayId: string } | null) =>
                  onGenderChange(
                    (value?.id as Gender | "") || ("" as Gender | "")
                  )
                }
              />
            </FormControl>
          </GroupContainer>
        </Box>
        {(showSaveFooter ?? !showToolbox) && (
          <FormSaveFooter
            onSave={() => onSave()}
            label={langStore.saveEntity}
            disabled={saving}
          />
        )}
      </Grid>
    );
  }
);
